import styled from '@emotion/styled';
import { MenuItem, Page, SideBarOpen } from './Sidebar';

import { colors } from 'styles/theme/colors';

import { useState, useMemo, useCallback, memo } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { useLocation } from 'react-router-dom';
import { useCheckAccess } from 'features/accessControl/components/AccessControl';
import { useFeatureFlag } from 'features/accessControl/hooks/useFeatureFlag';
import { useModule } from 'features/accessControl/hooks/useModule';
import { useOrgType } from 'features/accessControl/hooks/useOrg';
import { iframePaths } from 'features/iframe/utils';
import { Text } from 'components/text/Text';
import { Jargon } from 'components/jargon/Jargon';

export interface ExpandableMenuItemProps {
  item: MenuItem;
  onMenuClick: () => void;
  isSideBarOpen: SideBarOpen;
}

const ExpandableMenuItemComponent = ({
  item: { topPadding, MenuIcon, text, subItems, accessTo, featureflag, module, url, roles },
  isSideBarOpen,
  onMenuClick,
}: ExpandableMenuItemProps) => {
  const [open, setOpen] = useState(false);
  const isSupplier = useOrgType(2);
  const ff = useFeatureFlag(featureflag || '');
  const roleAccess = useCheckAccess(false, url);
  const moduleAccess = useModule(module || '');
  const isOpenSideBar = isSideBarOpen !== 'closed';
  const isMenuOpen = open && isOpenSideBar;
  const location = useLocation();
  const selected = useMemo(
    () =>
      subItems?.some(item => {
        return (
          (item.url.startsWith(location.pathname) || location.pathname.startsWith(item.url)) &&
          location.pathname.length > 1
        );
      }) ?? false,
    [subItems, location.pathname]
  );
  const handleToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);
  // iframe pages are not available for supplier portal accounts
  if (iframePaths.includes(url) && isSupplier) return null;
  // feature flag check
  if (featureflag && !ff) return null;
  // RBAC check
  if (!roleAccess) return null;
  // module check, supplier portal accounts have access to all
  if (module && !moduleAccess && !isSupplier) return null;
  // only supplier can view
  if (accessTo === 'supplierOnly' && !isSupplier) return null;
  // only designer can view
  if (accessTo === 'designerOnly' && isSupplier) return null;

  return (
    <Wrapper isMenuOpen={isMenuOpen}>
      <Item
        topPadding={topPadding}
        selected={selected}
        extend={isOpenSideBar}
        onClick={handleToggle}
      >
        <IconCon>
          {MenuIcon && (
            <MenuIcon
              weight={selected ? 'fill' : undefined}
              color={selected ? colors.brand[400] : colors.grey[700]}
              strokeWidth="0.9px"
            />
          )}
        </IconCon>
        <TextWrapper extend={isOpenSideBar}>
          <Text color={selected ? 'primary' : 'tertiary'}>
            <Jargon text={text} />
          </Text>
          <CaretIconWrapper isMenuOpen={isMenuOpen}>
            <CaretDown size={20} />
          </CaretIconWrapper>
        </TextWrapper>
      </Item>
      <SubItemsContainer isMenuOpen={isMenuOpen}>
        {subItems?.map(item => (
          <Page
            key={item.url}
            item={item}
            onMenuClick={onMenuClick}
            marginLeft={48}
            isSideBarOpen={isSideBarOpen}
          />
        ))}
      </SubItemsContainer>
    </Wrapper>
  );
};

export const ExpandableMenuItem = memo(ExpandableMenuItemComponent);
ExpandableMenuItem.displayName = 'ExpandableMenuItem';

const Item = styled.div<{ topPadding?: boolean; selected: boolean; extend: boolean }>`
  position: relative;
  width: ${p => (p.extend ? `100%` : `40px`)};
  transition: width 0.2s ease-in-out;
  display: flex;
  gap: ${p => p.theme.s.xs};
  align-items: center;
  cursor: pointer;
  flex-direction: row;
  height: 40px;
  ${p => (p.topPadding ? 'margin-top: 40px;' : '')}
  ${p => (p.selected ? `background-color: ${p.theme.palette.bg.brandSubtle};` : '')}
  border-radius: 8px;
  :hover {
    background-color: ${p => p.theme.palette.bg.primaryHover};
  }
`;

const IconCon = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  z-index: 1;
`;

const TextWrapper = styled.div<{ extend: boolean }>`
  position: absolute;
  left: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  width: 100%;
  opacity: ${p => (p.extend ? 1 : 0)};
  max-width: ${p => (p.extend ? 'calc(100% - 48px)' : '0')};
  gap: ${p => p.theme.s.xs};
  transition:
    opacity 0.2s ease-in-out,
    max-width 0.2s ease-in-out;
`;

const Wrapper = styled.div<{ isMenuOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${p => (p.isMenuOpen ? 6 : 0)}px;
`;

const CaretIconWrapper = styled.div<{ isMenuOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    opacity 0.2s ease-in-out,
    transform 0.3s ease-in-out;
  transform: ${p => (p.isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  &:hover {
    opacity: 0.7;
  }
`;

const SubItemsContainer = styled.div<{ isMenuOpen: boolean }>`
  overflow: hidden;
  max-height: ${p => (p.isMenuOpen ? '500px' : '0')};
  opacity: ${p => (p.isMenuOpen ? 1 : 0)};
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
